import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMe, getCurrentSubscription, getPlans, exportUserData, deleteAccount } from "@/api/endpoints";
import { UpgradeDialog } from "@/components/upgrade-dialog";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, CreditCard, Crown, Download, Trash2, Shield, CheckCircle2, XCircle } from "lucide-react";

export function SettingsPage() {
  const { logout } = useAuth();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Check payment result from URL
  const [paymentResult, setPaymentResult] = useState(() => new URLSearchParams(window.location.search).get("payment"));
  const [creditPaymentResult, setCreditPaymentResult] = useState(() => new URLSearchParams(window.location.search).get("credit_payment"));
  const creditAmount = new URLSearchParams(window.location.search).get("credits");

  useEffect(() => {
    if (paymentResult || creditPaymentResult) {
      window.history.replaceState({}, "", window.location.pathname);
      // After a successful payment the venue's planType (and aiCredits) just
      // changed server-side — invalidate so the page reflects it immediately.
      if (paymentResult === "success" || creditPaymentResult === "success") {
        qc.invalidateQueries({ queryKey: ["subscriptions"] });
        qc.invalidateQueries({ queryKey: ["venues"] });
        qc.invalidateQueries({ queryKey: ["ai-credits"] });
      }
    }
  }, [paymentResult, creditPaymentResult, qc]);

  const { data: user } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
  });

  const { data: subscriptions } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: getCurrentSubscription,
  });


  const { data: plans } = useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportUserData();
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "heremenu-verilerim.json";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== t("delete_confirm_text")) return;
    setDeleting(true);
    try {
      await deleteAccount();
      logout();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">{t("membership_settings")}</h1>

      {/* Profile */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="size-5" />
            {t("profile")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {user ? (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("name")}</span>
                <span className="font-medium">{user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("email")}</span>
                <span className="font-medium">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("registration_date")}</span>
                <span className="font-medium">
                  {new Date(user.createdAt).toLocaleDateString("tr-TR")}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">{t("loading")}</p>
          )}
        </CardContent>
      </Card>

      {/* Subscriptions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="size-5" />
            {t("subscriptions")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions?.length === 0 ? (
            <p className="text-muted-foreground">{t("no_subscription")}</p>
          ) : (
            <div className="space-y-3">
              {subscriptions?.map((sub) => (
                <div key={sub.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">{sub.name}</p>
                    {sub.planExpiresAt && (
                      <p className="text-sm text-muted-foreground">
                        {t("expiry")}: {new Date(sub.planExpiresAt).toLocaleDateString("tr-TR")}
                      </p>
                    )}
                  </div>
                  <Badge variant={sub.planType === "FREE" ? "secondary" : "default"}>
                    {sub.planType}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Crown className="size-5" />
            {t("plans")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            {plans?.map((plan) => {
              const isCurrentPlan = subscriptions?.[0]?.planType === plan.type;
              return (
                <div
                  key={plan.type}
                  className={`rounded-xl border-2 p-5 ${
                    plan.type === "PRO" ? "border-primary" : "border-border"
                  }`}
                >
                  <p className="font-bold text-lg">{plan.name}</p>
                  {(plan.priceYearly ?? 0) > 0 ? (
                    <p className="text-2xl font-bold text-primary mt-1">
                      {lang === "tr" ? `${plan.priceTryYearly || plan.priceYearly} ₺` : `$${plan.priceYearly}`}
                      <span className="text-sm font-normal text-muted-foreground">{t("per_year")}</span>
                    </p>
                  ) : (
                    <p className="text-2xl font-bold text-primary mt-1">{t("free")}</p>
                  )}
                  {plan.description && <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>}
                  {isCurrentPlan ? (
                    <Badge className="mt-3" variant="secondary">{t("active")}</Badge>
                  ) : (plan.priceYearly ?? 0) > 0 ? (
                    <Button size="sm" className="mt-3 w-full" onClick={() => setUpgradeOpen(true)}>
                      {t("upgrade_cta")}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="size-5" />
            {t("data_privacy")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="font-medium">{t("export_data")}</p>
              <p className="text-sm text-muted-foreground">
                {t("export_data_desc")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
              <Download className="mr-1 size-4" />
              {exporting ? t("preparing_download") : t("download")}
            </Button>
          </div>

          <div className="rounded-lg border border-destructive/30 p-3">
            <p className="font-medium text-destructive">{t("delete_account")}</p>
            <p className="mb-3 text-sm text-muted-foreground">
              {t("delete_account_desc")}
            </p>
            <div className="flex gap-2">
              <Input
                placeholder={t("delete_confirm_placeholder")}
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deleteConfirm !== t("delete_confirm_text") || deleting}
              >
                <Trash2 className="mr-1 size-4" />
                {deleting ? t("deleting") : t("delete")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment result popup */}
      {paymentResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPaymentResult(null)}>
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className={`mx-auto mb-3 flex size-14 items-center justify-center rounded-full ${paymentResult === "success" ? "bg-green-100" : "bg-red-100"}`}>
              {paymentResult === "success" ? <CheckCircle2 className="size-7 text-green-600" /> : <XCircle className="size-7 text-red-500" />}
            </div>
            <p className={`text-lg font-bold ${paymentResult === "success" ? "text-green-700" : "text-red-600"}`}>
              {paymentResult === "success" ? t("payment_success") : t("payment_failed")}
            </p>
            <Button className="mt-4 w-full" variant={paymentResult === "success" ? "default" : "outline"} onClick={() => setPaymentResult(null)}>
              {t("close")}
            </Button>
          </div>
        </div>
      )}

      {/* Credit payment result popup */}
      {creditPaymentResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setCreditPaymentResult(null)}>
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className={`mx-auto mb-3 flex size-14 items-center justify-center rounded-full ${creditPaymentResult === "success" ? "bg-green-100" : "bg-red-100"}`}>
              {creditPaymentResult === "success" ? <CheckCircle2 className="size-7 text-green-600" /> : <XCircle className="size-7 text-red-500" />}
            </div>
            <p className={`text-lg font-bold ${creditPaymentResult === "success" ? "text-green-700" : "text-red-600"}`}>
              {creditPaymentResult === "success"
                ? t("credit_purchase_success").replace("{{credits}}", creditAmount || "")
                : t("credit_purchase_failed")}
            </p>
            <Button className="mt-4 w-full" variant={creditPaymentResult === "success" ? "default" : "outline"} onClick={() => setCreditPaymentResult(null)}>
              {t("close")}
            </Button>
          </div>
        </div>
      )}

      <UpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
